import { t } from "i18next";
import AdvancedTaskDto from "../types/dtos/forms/AdvancedTaskDto";
import { HighlightedAdvancedTasks } from "../types/dtos/tasks/advanced-tasks/HighlightedAdvancedTasksCount";
import { TaskManagementDropDownMenuItemDto } from "../types/tasks/TaskManagementDropDownMenuItemDto";
import { TaskManagementDropDownMenuArgs } from "../types/tasks/TaskManagementDropDownMenuArgs";
import { ClientTaskType } from "../types/dtos/tasks/advanced-tasks/ClientTaskType";
import { UserContextInterface } from "../state/UserContext";
import { FormType, ValidationSettings } from "../types/forms";
import { CountRestrictionType } from "../types/dtos/tasks/advanced-tasks/CountRestrictionType";
import {
  SimpleFormAnswer,
  SimpleFormVersionContent,
  SimpleFormQuestion,
} from "../types/dtos/simple-forms";
import AdvancedTaskQuestionMode from "../types/dtos/forms/AdvancedTaskQuestionMode";
import AdvancedTaskTypeSwapabilityDto from "../types/dtos/forms/AdvancedTaskTypeSwapabilityDto";
import AdvTaskListingDto from "../types/dtos/dashboards/manager/listings/adv-tasks/AdvTaskListingDto";
import { AdvTaskOverallStatusType } from "../types/dtos/dashboards/manager/listings/adv-tasks/AdvTaskOverallStatusType";
import {
  isReviewQuestionMode,
  usesCurrentTasksList,
} from "./advancedTaskQuestionModeHelper";

export const advancedTaskHelper = {
  /* Method to retrieve the translated lower case text of singular task name **/
  ToLowerCase: (translationKeyIdentifier: string): string => {
    const translatedText = t(translationKeyIdentifier);

    if (translatedText !== translatedText.toUpperCase()) {
      return translatedText.toLowerCase();
    }

    return translatedText;
  },

  /** Use the TaskType configuration, and the question validation settings, to determine the validation count for the number of tasks allowed to be created.
   * Sometimes clients don't want the dashboard min/max, but do want to enforce at least one task, or a maximum of 3 tasks, etc in a collab doc/journey.
   */
  getValidationCounts(
    /** The min count defined against the TaskType */
    taskTypeMinCount: number | null,
    /** The max count defined against the TaskType */
    taskTypeMaxCount: number | null,
    questionValidations: ValidationSettings
  ): {
    min: number | null;
    max: number | null;
    restrictionType: CountRestrictionType;
  } {
    // By default, use the task type config for min/max
    let min = taskTypeMinCount;
    let max = taskTypeMaxCount;
    let restrictionType: CountRestrictionType = "NONE";

    // If question-level validations are specified, override the task type config for min/max active tasks
    if (questionValidations) {
      if (questionValidations.min != null && questionValidations.min >= 0) {
        min = questionValidations.min;
      }
      if (questionValidations.max != null && questionValidations.max > 0) {
        max = questionValidations.max;
      }
    }

    // Calculate the restriction type
    if (min && max) {
      restrictionType = "MIN-AND-MAX";
    } else if (min && !max) {
      restrictionType = "MIN-ONLY";
    } else if (!min && max) {
      restrictionType = "MAX-ONLY";
    }

    return {
      min,
      max,
      restrictionType,
    };
  },

  /** Creates a copy of the current highlights minus the task to be un-highlighted */
  removeTaskHighlightForUserContext: (
    loggedInUserId: number,
    taskOwnerUserId: number,
    taskType: string,
    taskId: string,
    currentHighlights: HighlightedAdvancedTasks[]
  ): HighlightedAdvancedTasks[] => {
    if (!currentHighlights) {
      return [];
    }
    const output: HighlightedAdvancedTasks[] = [...currentHighlights];
    const matchingTaskTypeIndex = output.findIndex(
      (x) => x.taskTypeId === taskType
    );

    if (matchingTaskTypeIndex >= 0) {
      if (loggedInUserId === taskOwnerUserId) {
        // The task to be cleared is one owned by the logged in user
        const ownHighlightedTaskIds = output[matchingTaskTypeIndex].ownTaskIds;
        const matchingTaskIdIndex = ownHighlightedTaskIds.findIndex(
          (x) => x === taskId
        );
        if (matchingTaskIdIndex >= 0) {
          ownHighlightedTaskIds.splice(matchingTaskIdIndex, 1);
        }
      } else {
        // The task to be cleared belongs to someone managed by the logged in user
        const managerHighlightedTaskIds =
          output[matchingTaskTypeIndex].managerTaskIds;
        const matchingTaskIdIndex = managerHighlightedTaskIds.findIndex(
          (x) => x === taskId
        );
        if (matchingTaskIdIndex >= 0) {
          managerHighlightedTaskIds.splice(matchingTaskIdIndex, 1);
        }
      }
    }

    return output;
  },
  /* Gets the drop down menu options for the 'Actions' button for the TaskManagementQuestion **/
  getDropDownMenuOptions: (
    task: AdvancedTaskDto,
    taskType: ClientTaskType | undefined,
    formType: FormType,
    commentsRequired: boolean,
    questionMode: AdvancedTaskQuestionMode,
    isLoggedInUserTheSubjectUser: boolean,
    subjectUsersName: string,
    isTaskTypeIdSwapable?: boolean,
    swapabilityInfo?: AdvancedTaskTypeSwapabilityDto,
    isReviewQuestion?: boolean,
    isSettingQuestion?: boolean,
    isDualPrepPlanningForm?: boolean
  ): TaskManagementDropDownMenuItemDto[] => {
    const menuItems: TaskManagementDropDownMenuItemDto[] = [];

    const questionModeIsTaskReview = isReviewQuestionMode(questionMode);

    // [Review and rate Task] - Only show when the task is completed AND the owner and manager have
    // commented AND taskType has a review form AND only show in the collab doc (employees shouldn't
    // rate before a convo has taken place). Dual-prep planning defers rating to the meeting.
    if (
      task.status === "COMPLETED" &&
      taskType !== undefined &&
      taskType.reviewFormId !== null &&
      formType === "COLLAB-DOC" &&
      !isDualPrepPlanningForm &&
      (!commentsRequired ||
        (task.enforcedComments != null &&
          task.enforcedComments.areOwnerCountConditionsSatisfied &&
          task.enforcedComments.areManagerRoleCountConditionsSatisfied))
    ) {
      const singularTaskTypeName = advancedTaskHelper.ToLowerCase(
        taskType.singularNameTranslationKeyIdentifier
      );
      menuItems.push({
        text: t("TaskType.Common.ReviewAndRateTask", {
          taskType: singularTaskTypeName,
        }),
        customArg: TaskManagementDropDownMenuArgs.ReviewTask,
        disabled: false,
        subText: taskType
          ? t("TaskType.ActionOptions.SubText.ReviewAndRateTask", {
              task: advancedTaskHelper.ToLowerCase(
                t(taskType.singularNameTranslationKeyIdentifier)
              ),
            })
          : undefined,
      });
    }

    // [Comment on progress]
    // In the scenario where comments are enforced but we are in review mode
    // we want to only show the comment option if the user is the manager
    // and if they haven't commented yet (e.g. 'reviewed' yet) and the task is completed
    if (
      commentsRequired &&
      questionModeIsTaskReview &&
      !isLoggedInUserTheSubjectUser &&
      task.enforcedComments.managerRoleCount == 0 &&
      task.status == "COMPLETED"
    ) {
      menuItems.push({
        text: t(
          "TaskType.EnforceComments.ReviewOnly.PleaseCommentOnNamesProgress",
          {
            name: subjectUsersName,
            taskTypeSingularName:
              taskType != null
                ? advancedTaskHelper.ToLowerCase(
                    taskType.singularNameTranslationKeyIdentifier
                  )
                : "",
          }
        ),
        customArg: TaskManagementDropDownMenuArgs.AddComment,
        subText: t("TaskType.ActionOptions.SubText.Ongoing"),
        disabled: false,
      });
    }
    // Only show when the task is not cancelled
    // and the question type isn't a review only (as in this scenario the user should be signposted
    // towards either closing or cancelling the task)
    else if (
      task.status !== "CANCELLED" &&
      questionMode !== "TASK-REVIEW-ONLY"
    ) {
      menuItems.push({
        text: t("TaskType.Common.CommentOnProgress"),
        customArg: TaskManagementDropDownMenuArgs.AddComment,
        disabled: false,
        subText:
          task.status !== "COMPLETED"
            ? t("TaskType.ActionOptions.SubText.Ongoing")
            : t("TaskType.ActionOptions.SubText.AddYourComment"),
      });
    }

    if (task.status === "ACTIVE" && taskType !== undefined) {
      const singularTaskTypeName = advancedTaskHelper.ToLowerCase(
        taskType.singularNameTranslationKeyIdentifier
      );
      // [Complete Task] - Only show when the task is active and there is a taskType (as we need the name)
      // Don't show the complete task option if we are in task creation mode only.
      if (questionMode !== "TASK-CREATION-ONLY") {
        menuItems.push({
          text: t("TaskType.Common.CompleteTask", {
            taskType: singularTaskTypeName,
          }),
          customArg: TaskManagementDropDownMenuArgs.CompleteTask,
          disabled: false,
          subText: t("TaskType.ActionOptions.Sub.Text.MarkAsDone"),
        });
      }

      // [Cancel Task] - Only show when the task is active and there is a taskType (as we need the name)
      menuItems.push({
        text: t("TaskType.Common.CancelTask", {
          taskType: singularTaskTypeName,
        }),
        customArg: TaskManagementDropDownMenuArgs.CancelTask,
        disabled: false,
        subText: taskType
          ? t("TaskType.ActionOptions.SubText.CancelThisTask", {
              task: advancedTaskHelper.ToLowerCase(
                t(taskType.singularNameTranslationKeyIdentifier)
              ),
            })
          : undefined,
      });
    }

    // [Re-open Task] - Only show when the task has been completed or cancelled and there is a taskType (as we need the name)
    if (
      (task.status == "COMPLETED" || task.status == "CANCELLED") &&
      taskType !== undefined
    ) {
      const singularTaskTypeName = advancedTaskHelper.ToLowerCase(
        taskType.singularNameTranslationKeyIdentifier
      );
      menuItems.push({
        text: t("TaskType.Common.ReopenTask", {
          taskType: singularTaskTypeName,
        }),
        customArg: TaskManagementDropDownMenuArgs.ReopenTask,
        disabled: false,
        subText: t("TaskType.ActionOptions.SubText.Reopen"),
      });
    }

    // [View details / Edit details]
    let viewEditOptionSubText: string | undefined = undefined;
    if (taskType) {
      viewEditOptionSubText =
        task.status === "CANCELLED" || task.status === "COMPLETED"
          ? t("TaskType.ActionOptions.SubText.ViewDetailsOfTask", {
              task: advancedTaskHelper.ToLowerCase(
                t(taskType.singularNameTranslationKeyIdentifier)
              ),
            })
          : t("TaskType.ActionOptions.SubText.EditDetails", {
              task: advancedTaskHelper.ToLowerCase(
                t(taskType.singularNameTranslationKeyIdentifier)
              ),
            });
    }

    // [Swap Task] - Show swap options if task type is swapable, task is eligible, and we're in the right question
    // Don't show swap options for cancelled tasks
    if (
      isTaskTypeIdSwapable &&
      task.isEligibleForSectionSwap &&
      task.status !== "CANCELLED" &&
      swapabilityInfo
    ) {
      // Special case: If both isReviewQuestion and isSettingQuestion are true,
      // show only one "treat as" option based on isNewlyCreatedInThisJourney
      if (isReviewQuestion && isSettingQuestion && taskType != null) {
        const singularTaskTypeName = advancedTaskHelper.ToLowerCase(
          taskType.singularNameTranslationKeyIdentifier
        );
        
        // If task is currently marked as newly created, show option to treat as existing
        if (task.isNewlyCreatedInThisJourney) {
          menuItems.push({
            text: t("TaskType.Common.MoveToReviewQuestion", {
              TaskTypeSingularName: advancedTaskHelper.ToLowerCase(
                t(taskType.singularNameTranslationKeyIdentifier)
              ),
            }),
            customArg: TaskManagementDropDownMenuArgs.SwapTask_ToReview,
            disabled: false,
            subText: t("TaskType.ActionOptions.SubText.MoveToReviewQuestion"),
          });
        } else {
          // If task is not marked as newly created, show option to treat as new
          menuItems.push({
            text: t("TaskType.Common.MoveToSettingQuestion", {
              TaskTypeSingularName: advancedTaskHelper.ToLowerCase(
                t(taskType.singularNameTranslationKeyIdentifier)
              ),
            }),
            customArg: TaskManagementDropDownMenuArgs.SwapTask_ToNew,
            disabled: false,
            subText: t("TaskType.ActionOptions.SubText.MoveToSettingQuestion"),
          });
        }
      } else {
        // Normal case: show swap options based on which question we're in
        // If we're in the review question, show option to move to setting question
        if (isReviewQuestion && taskType != null) {
          menuItems.push({
            text: t("TaskType.Common.MoveToSettingQuestion", {
              TaskTypeSingularName: advancedTaskHelper.ToLowerCase(
                t(taskType.singularNameTranslationKeyIdentifier)
              ),
            }),
            customArg: TaskManagementDropDownMenuArgs.SwapTask_ToNew,
            disabled: false,
            subText: t("TaskType.ActionOptions.SubText.MoveToSettingQuestion"),
          });
        }
        // If we're in the setting question, show option to move to review question
        if (isSettingQuestion && taskType != null) {
          menuItems.push({
            text: t("TaskType.Common.MoveToReviewQuestion", {
              TaskTypeSingularName: advancedTaskHelper.ToLowerCase(
                t(taskType.singularNameTranslationKeyIdentifier)
              ),
            }),
            customArg: TaskManagementDropDownMenuArgs.SwapTask_ToReview,
            disabled: false,
            subText: t("TaskType.ActionOptions.SubText.MoveToReviewQuestion"),
          });
        }
      }
    }

    menuItems.push({
      text:
        task.status === "COMPLETED" ||
        task.status === "CANCELLED" ||
        questionModeIsTaskReview
          ? t("Common.ViewDetails")
          : t("Common.EditDetails"),
      customArg: TaskManagementDropDownMenuArgs.ViewDetails,
      disabled: false,
      subText: viewEditOptionSubText,
    });

    return menuItems;
  },

  taskIsHighlightedForUser: (
    userContext: UserContextInterface,
    task: AdvancedTaskDto,
    isReadOnly: boolean
  ): boolean => {
    // If the journey/collab doc has been completed and is in read only then don't show the highlight dot
    // as they won't be able to clear it from here as the buttons don't show
    if (isReadOnly) {
      return false;
    }

    if (task.highlightToOwner && task.ownerEmployeeId === userContext.user.id) {
      return true;
    }

    if (
      task.highlightToManager &&
      task.ownerEmployeeId !== userContext.user.id
    ) {
      return true;
    }

    return false;
  },

  /** Returns the AutomaticTitleSettingQuestion if one is specified against the TaskType */
  getAutomaticTitleSettingQuestion: (
    clientTaskType: ClientTaskType,
    formContent: SimpleFormVersionContent
  ): SimpleFormQuestion | null => {
    if (
      formContent?.questions &&
      clientTaskType.titleSettingQuestionId !== null
    ) {
      const titleQuestion = formContent.questions.find(
        (x) => x.questionId === clientTaskType.titleSettingQuestionId
      );

      if (titleQuestion) {
        return titleQuestion;
      }
    }
    return null;
  },

  /** If a titleSettingQuestion is specified against the TaskType, look for an answer to that question, and populate the UserTask's Title field with that value.
   * Returns true if the title was updated, false if it was not.
   */
  conditionallyAutomaticallySetTaskTitle: (
    clientTaskType: ClientTaskType,
    formContent: SimpleFormVersionContent,
    latestAnswers: SimpleFormAnswer[],
    currentTitle: string | undefined,
    onSetTaskTitle: (newTitle: string) => void
  ): boolean => {
    const titleSettingQuestion =
      advancedTaskHelper.getAutomaticTitleSettingQuestion(
        clientTaskType,
        formContent
      );
    if (titleSettingQuestion) {
      const answer = latestAnswers.find(
        (x) => x.questionId === titleSettingQuestion.questionId
      );
      if (
        answer &&
        answer.multiChoiceAnswers &&
        answer.multiChoiceAnswers.length > 0
      ) {
        // Find the multi choice answer value
        const multiChoiceAnswer = answer.multiChoiceAnswers[0];

        // Use the "Custom" text if it exists, otherwise use the selected option's display value (translated, where relevant)
        let answerDisplayValue: string | null = null;
        if (
          multiChoiceAnswer.customOtherText &&
          multiChoiceAnswer.customOtherText.length > 0
        ) {
          answerDisplayValue = multiChoiceAnswer.customOtherText;
        } else {
          const option = titleSettingQuestion.options?.find(
            (x) => x.optionId === multiChoiceAnswer.selectedOptionId
          );
          if (option) {
            answerDisplayValue =
              option.translateDisplayValue &&
              option.displayValueTranslationKeyIdentifier
                ? t(option.displayValueTranslationKeyIdentifier)
                : option.rawDisplayValue;
          }
        }

        const backupValue = "[Not set]"; // Logic dictates this should never be necessary, but this easily traceable value will help us debug if it ever happens
        const newTitleValue = answerDisplayValue
          ? answerDisplayValue
          : backupValue;
        if (newTitleValue !== currentTitle) {
          onSetTaskTitle(newTitleValue);
          return true;
        }
      }
    }

    return false;
  },

  /**
   * The tasks a question mode actually displays. Combined modes see current and new;
   * check-in/review see current only; creation-only sees new only. Overdue and
   * review-form checks have to use this list or they leak across sections that
   * share a task type.
   */
  getTaskManagementQuestionTaskSet: (config: {
    questionMode: AdvancedTaskQuestionMode;
    tasks: AdvancedTaskDto[];
    newTasks: AdvancedTaskDto[];
    liveAndNewTasks: AdvancedTaskDto[];
  }): AdvancedTaskDto[] => {
    if (config.questionMode === "TASK-CREATION-ONLY") {
      return config.newTasks;
    }
    if (usesCurrentTasksList(config.questionMode)) {
      return config.tasks;
    }
    return config.liveAndNewTasks;
  },

  /**
   * Cancelled tasks the question should list. API CancelledTasks includes every cancelled
   * task (including journey-created ones). Current-list modes only show pre-existing
   * cancelled tasks so prep-created work stays in Creation-only.
   */
  getCancelledTasksForQuestion: (config: {
    questionMode: AdvancedTaskQuestionMode;
    cancelledTasks: AdvancedTaskDto[];
  }): AdvancedTaskDto[] => {
    if (config.questionMode === "TASK-CREATION-ONLY") {
      return [];
    }
    if (usesCurrentTasksList(config.questionMode)) {
      return config.cancelledTasks.filter(
        (task) => !task.isNewlyCreatedInThisJourney
      );
    }
    return config.cancelledTasks;
  },

  /**
   * Whether the question should render its task table vs the empty state.
   * Must match which lists the UI actually maps: current-list modes ignore newTasks
   * and journey-created cancelled tasks.
   */
  questionHasTasksToShow: (config: {
    questionMode: AdvancedTaskQuestionMode;
    tasks: AdvancedTaskDto[];
    cancelledTasks: AdvancedTaskDto[];
    newTasks: AdvancedTaskDto[];
  }): boolean => {
    if (config.questionMode === "TASK-CREATION-ONLY") {
      return config.newTasks.length > 0;
    }
    const cancelledToShow = advancedTaskHelper.getCancelledTasksForQuestion({
      questionMode: config.questionMode,
      cancelledTasks: config.cancelledTasks,
    });
    if (usesCurrentTasksList(config.questionMode)) {
      return config.tasks.length > 0 || cancelledToShow.length > 0;
    }
    return (
      config.tasks.length > 0 ||
      cancelledToShow.length > 0 ||
      config.newTasks.length > 0
    );
  },

  /**
   * Whether the rating column belongs on this task management question.
   * Dual-prep planning hides it entirely so people cannot rate before the meeting.
   */
  shouldDisplayTaskRatingColumn: ({
    showRatingColumn,
    formType,
    hasReviewForm,
    isDualPrepPlanningForm,
  }: {
    showRatingColumn: boolean;
    formType: FormType;
    hasReviewForm: boolean;
    isDualPrepPlanningForm: boolean;
  }): boolean => {
    if (!showRatingColumn || isDualPrepPlanningForm) {
      return false;
    }
    if (formType === "JOURNEY") {
      return hasReviewForm;
    }
    return true;
  },

  /**
   * Where a click on the rating cell should go for a completed, unrated task.
   * Missing enforced-comments data means comments are not being chased on this
   * question, so the click goes straight to the review form.
   */
  getCompletedTaskRatingAction: (
    task: AdvancedTaskDto,
    isLoggedInUserTheSubjectUser: boolean
  ): string | null => {
    if (task.status !== "COMPLETED") {
      return null;
    }

    if (task.enforcedComments == null) {
      return TaskManagementDropDownMenuArgs.ReviewTask;
    }

    if (isLoggedInUserTheSubjectUser) {
      if (!task.enforcedComments.areOwnerCountConditionsSatisfied) {
        return TaskManagementDropDownMenuArgs.AddComment;
      }
      if (task.enforcedComments.areManagerRoleCountConditionsSatisfied) {
        return TaskManagementDropDownMenuArgs.ReviewTask;
      }
      return null;
    }

    if (!task.enforcedComments.areManagerRoleCountConditionsSatisfied) {
      return TaskManagementDropDownMenuArgs.AddComment;
    }
    return TaskManagementDropDownMenuArgs.ReviewTask;
  },

  /**
   * Determines if a rating should be clickable based on task status and enforced comments conditions
   */
  canUserInteractWithRating: (
    task: AdvancedTaskDto,
    showExtraColumnsBecauseOfEnforcedComments: boolean,
    isLoggedInUserTheSubjectUser: boolean
  ): boolean => {
    return (
      task.status === "COMPLETED" &&
      (!showExtraColumnsBecauseOfEnforcedComments ||
        (task.enforcedComments != null &&
          task.enforcedComments.areOwnerCountConditionsSatisfied &&
          task.enforcedComments.areManagerRoleCountConditionsSatisfied)) &&
      // Additional check: if subject user has satisfied their conditions but manager hasn't, don't make it clickable
      !(
        isLoggedInUserTheSubjectUser &&
        task.enforcedComments != null &&
        task.enforcedComments.areOwnerCountConditionsSatisfied &&
        !task.enforcedComments.areManagerRoleCountConditionsSatisfied
      )
    );
  },

  /**
   * Sorts tasks by status priority and target date for display in listings
   * Priority order: OVERDUE > ACTIVE > COMPLETED > CANCELLED > HISTORIC
   * Within same status: newer dates first
   */
  sortTasksForDisplay: (
    tasksToSort: AdvTaskListingDto[]
  ): AdvTaskListingDto[] => {
    return [...tasksToSort].sort((a, b) => {
      // Define status priority (lower number = higher priority)
      const statusPriority = (status: AdvTaskOverallStatusType): number => {
        switch (status) {
          case "OVERDUE":
            return 0; // Highest priority
          case "ACTIVE":
            return 1;
          case "COMPLETED":
            return 2;
          case "CANCELLED":
            return 3;
          case "HISTORIC":
            return 4; // Lowest priority
          default:
            return 5;
        }
      };

      // First, sort by status priority
      const priorityA = statusPriority(a.overallStatus);
      const priorityB = statusPriority(b.overallStatus);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // If same status, sort by target date (newer first - descending)
      const dateA = a.targetDate ? new Date(a.targetDate).getTime() : 0;
      const dateB = b.targetDate ? new Date(b.targetDate).getTime() : 0;

      // Newer dates first (descending), so B - A
      return dateB - dateA;
    });
  },
};

export default advancedTaskHelper;
